import React, { useEffect, useState } from "react";
import { Truck, MapPin, X } from "lucide-react";
import { axios, API } from "../lib/api";
import { useAuth } from "../auth/AuthProvider";
import { PayPalCheckoutButton } from "./PayPalCheckoutButton";

/**
 * Shipping checkout modal — collects buyer's UK address + service choice, then
 * triggers Stripe checkout via /api/orders/checkout. On success, redirects to
 * Stripe's hosted page.
 */
export const ShippingCheckoutModal = ({ listing, onClose }) => {
  const { token, user } = useAuth();
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState("tracked_48");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [addr, setAddr] = useState({
    full_name: "", email: "", phone: "",
    address_line1: "", address_line2: "",
    city: "", county: "", postcode: "", country_code: "GB"
  });

  useEffect(() => {
    axios.get(`${API}/shipping/services`).then(r => setServices(r.data.services)).catch(() => {});
  }, []);

  const selectedService = services.find(s => s.id === serviceId) || { price: 0 };
  const SHIPPING_MARKUP = 0.50;  // Matches backend SHIPPING_MARKUP_GBP constant
  const itemPrice = listing?.price || 0;
  const shippingFloor = selectedService.price || 0;
  const shippingTotal = shippingFloor + (shippingFloor > 0 ? SHIPPING_MARKUP : 0);
  const total = (itemPrice + shippingTotal).toFixed(2);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setErr("");
    try {
      const res = await axios.post(`${API}/orders/checkout`, {
        listing_id: listing.id,
        origin_url: window.location.origin,
        shipping_service: serviceId,
        shipping_address: addr,
      }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = res.data.checkout_url;
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Checkout failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-auto" data-testid="shipping-checkout-modal">
      <div className="brutalist-card bg-white p-6 max-w-lg w-full my-8">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-2xl font-black flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Truck className="w-6 h-6 text-[#FF6B6B]" /> Shipping & Checkout
            </h2>
            <p className="text-xs text-gray-500 mt-1">UK delivery via Royal Mail</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full" aria-label="Close" data-testid="checkout-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input required placeholder="Full name" value={addr.full_name}
              onChange={e => setAddr({ ...addr, full_name: e.target.value })}
              className="brutalist-input col-span-2" data-testid="ship-fullname" />
            <input required type="email" placeholder="Email" value={addr.email}
              onChange={e => setAddr({ ...addr, email: e.target.value })} className="brutalist-input" data-testid="ship-email" />
            <input placeholder="Phone (optional)" value={addr.phone}
              onChange={e => setAddr({ ...addr, phone: e.target.value })} className="brutalist-input" data-testid="ship-phone" />
            <input required placeholder="Address line 1" value={addr.address_line1}
              onChange={e => setAddr({ ...addr, address_line1: e.target.value })}
              className="brutalist-input col-span-2" data-testid="ship-line1" />
            <input placeholder="Address line 2 (optional)" value={addr.address_line2}
              onChange={e => setAddr({ ...addr, address_line2: e.target.value })}
              className="brutalist-input col-span-2" />
            <input required placeholder="City" value={addr.city}
              onChange={e => setAddr({ ...addr, city: e.target.value })} className="brutalist-input" data-testid="ship-city" />
            <input placeholder="County" value={addr.county}
              onChange={e => setAddr({ ...addr, county: e.target.value })} className="brutalist-input" />
            <input required placeholder="Postcode" value={addr.postcode}
              onChange={e => setAddr({ ...addr, postcode: e.target.value.toUpperCase() })}
              className="brutalist-input" data-testid="ship-postcode" />
            <select value={addr.country_code} onChange={e => setAddr({ ...addr, country_code: e.target.value })}
              className="brutalist-input" data-testid="ship-country">
              <option value="GB">United Kingdom</option>
              <option value="OTHER">Other (manual fulfillment)</option>
            </select>
          </div>

          <div className="border-t-2 border-gray-900 pt-3">
            <h3 className="font-bold text-sm uppercase mb-2 flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Choose delivery
            </h3>
            <div className="space-y-2" data-testid="ship-services">
              {services.map(s => (
                <label key={s.id} className={`flex items-center justify-between p-3 border-2 rounded-xl cursor-pointer ${serviceId === s.id ? 'border-[#FF6B6B] bg-[#FF6B6B]/5' : 'border-gray-200'}`}>
                  <div className="flex items-center gap-3">
                    <input type="radio" name="svc" value={s.id} checked={serviceId === s.id}
                      onChange={() => setServiceId(s.id)} data-testid={`ship-service-${s.id}`} />
                    <div>
                      <div className="font-bold text-sm">{s.label}</div>
                      <div className="text-xs text-gray-500">{s.description}</div>
                    </div>
                  </div>
                  <span className="font-black">£{s.price.toFixed(2)}</span>
                </label>
              ))}
            </div>
            {shippingFloor > 0 && (
              <p className="text-[11px] text-gray-500 mt-2">
                A £{SHIPPING_MARKUP.toFixed(2)} handling fee is added on top of Royal Mail's price.
              </p>
            )}
          </div>

          {itemPrice > 0 && (
            <div className="space-y-1 text-sm" data-testid="ship-breakdown">
              <div className="flex justify-between"><span>Item</span><span>£{itemPrice.toFixed(2)}</span></div>
              <div className="flex justify-between text-gray-600">
                <span>Royal Mail {selectedService.label || ""}</span>
                <span>£{shippingFloor.toFixed(2)}</span>
              </div>
              {shippingFloor > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>Handling</span>
                  <span>£{SHIPPING_MARKUP.toFixed(2)}</span>
                </div>
              )}
            </div>
          )}

          <div className="border-t-2 border-gray-900 pt-3 flex justify-between font-bold">
            <span>Total</span>
            <span data-testid="ship-total" className="text-[#FF6B6B] text-xl">£{total}</span>
          </div>

          {err && <p className="text-red-500 text-sm" data-testid="ship-error">{err}</p>}

          {/* Button order varies by A/B cohort: stripe_first (control) shows Stripe first;
              paypal_first (variant) flips them. Cohort is deterministic per user. */}
          {user?.checkout_cohort === "paypal_first" ? (
            <>
              <PayPalCheckoutButton
                listing={listing}
                serviceId={serviceId}
                shippingAddress={addr}
                total={total}
                disabled={!addr.full_name || !addr.email || !addr.address_line1 || !addr.city || !addr.postcode}
                onSuccess={(orderId) => {
                  if (typeof window !== "undefined") {
                    window.location.href = `/order-success?order_id=${orderId}&provider=paypal`;
                  }
                }}
              />
              <button type="submit" disabled={submitting} className="brutalist-btn-primary w-full" data-testid="ship-checkout-submit">
                {submitting ? "Redirecting…" : `Pay £${total} with Stripe`}
              </button>
            </>
          ) : (
            <>
              <button type="submit" disabled={submitting} className="brutalist-btn-primary w-full" data-testid="ship-checkout-submit">
                {submitting ? "Redirecting…" : `Pay £${total} with Stripe`}
              </button>
              <PayPalCheckoutButton
                listing={listing}
                serviceId={serviceId}
                shippingAddress={addr}
                total={total}
                disabled={!addr.full_name || !addr.email || !addr.address_line1 || !addr.city || !addr.postcode}
                onSuccess={(orderId) => {
                  if (typeof window !== "undefined") {
                    window.location.href = `/order-success?order_id=${orderId}&provider=paypal`;
                  }
                }}
              />
            </>
          )}

          <p className="text-[11px] text-gray-500 text-center">
            UK orders are dispatched via Royal Mail Click & Drop. Non-UK orders are flagged for manual fulfillment by the seller.
          </p>
        </form>
      </div>
    </div>
  );
};
